import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { BrandXS } from '@sorare/core/src/atoms/typography';
import { cart, transferMarket } from '@sorare/core/src/lib/glossary';

import { getSteppedPrimaryOffer } from '#src/hooks/primaryBuy/steppedPrimaryOffer.ts';

import type { PriceLockedRibbon_primaryOffer } from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: var(--ribbon-left, auto);
  right: var(--ribbon-right, auto);
  padding: var(--half-unit) var(--unit);
  background: var(--ribbon-color);
  border-radius: var(--ribbon-radius);
`;

type Props = {
  primaryOffer: PriceLockedRibbon_primaryOffer;
  position?: 'top-left' | 'top-right';
};

const SIDE_STYLES: Record<
  NonNullable<Props['position']>,
  Record<string, string>
> = {
  'top-left': { '--ribbon-left': '0', '--ribbon-radius': '0 8px 8px 0' },
  'top-right': { '--ribbon-right': '0', '--ribbon-radius': '8px 0 0 8px' },
};

export const PriceLockedRibbon = ({
  primaryOffer,
  position = 'top-right',
}: Props) => {
  const { isStepped, soldOut, color } = getSteppedPrimaryOffer(primaryOffer);

  if (!isStepped) return null;

  return (
    <Root
      style={
        { '--ribbon-color': color, ...SIDE_STYLES[position] } as CSSProperties
      }
    >
      <BrandXS color="var(--c-black)">
        <FormattedMessage
          {...(soldOut ? cart.soldOut : transferMarket.priceLocked)}
        />
      </BrandXS>
    </Root>
  );
};

PriceLockedRibbon.fragments = {
  primaryOffer: gql`
    fragment PriceLockedRibbon_primaryOffer on TokenPrimaryOffer {
      id
      ...SteppedPrimaryOffer_primaryOffer
    }
    ${getSteppedPrimaryOffer.fragments.primaryOffer}
  ` as TypedDocumentNode<PriceLockedRibbon_primaryOffer>,
};
